/**
 * useSocket — Phoenix Channels client (replaces Socket.io)
 *
 * Channel architecture (per docs/architecture/realtime.md):
 *   "agent:{userId}"     → AgentChannel (dashboard, status, queue)
 *   "call:{call_id}"     → CallChannel  (live call events)
 *   "campaign:{id}"      → CampaignChannel (dialer progress)
 *   "manager:dashboard"  → ManagerChannel (supervisor view)
 *   "admin:system"       → AdminChannel (system metrics)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Socket } from "phoenix";
import { SOCKET_URL } from "../apiConstants";
import { useAuth } from "../contexts/AuthContext";

const useSocket = () => {
  const { user, socketToken } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastCallStatus, setLastCallStatus] = useState(null);
  const [lastAgentStatus, setLastAgentStatus] = useState(null);
  const [lastAiInsight, setLastAiInsight] = useState(null);

  const socketRef = useRef(null);
  const channelsRef = useRef({});
  const cleanupRef = useRef([]);
  const errorCountRef = useRef(0);
  const socketTokenRef = useRef(socketToken);
  const hasSocketToken = Boolean(socketToken);
  socketTokenRef.current = socketToken;

  useEffect(() => {
    if (!user?.id || !socketTokenRef.current) {
      setIsConnected(false);
      setError(null);
      errorCountRef.current = 0;
      return undefined;
    }

    const socket = new Socket(SOCKET_URL, {
      // Phoenix calls this for every connection attempt, including reconnects.
      authToken: () => socketTokenRef.current,
      reconnectAfterMs: (tries) =>
        [1000, 2000, 5000, 10000][tries - 1] || 10000,
      heartbeatIntervalMs: 30000,
    });

    let openedTransport = null;
    socket.onOpen(() => {
      openedTransport = socket.conn;
      setIsConnected(true);
      setError(null);
      errorCountRef.current = 0;
    });

    socket.onClose((event) => {
      const wasOpened = openedTransport !== null && openedTransport === socket.conn;
      openedTransport = null;
      setIsConnected(false);
      // Do not turn a rejected handshake into a new normal-close retry loop.
      // Bandit acknowledges a remote close (including application codes) with
      // 1000. Phoenix otherwise suppresses recovery for that code even when
      // the application never disconnected. Reuse Phoenix's timer so its
      // backoff, visibility handling and disconnect cancellation still apply.
      if (wasOpened && event?.code === 1000 && !socket.closeWasClean) {
        socket.reconnectTimer.scheduleTimeout();
      }
    });

    socket.onError((err) => {
      errorCountRef.current += 1;
      // Only surface the error to UI after 3 consecutive failures to avoid spamming transient glitches
      if (errorCountRef.current >= 3) {
        setError("Real-time connection error — reconnecting…");
      }
      console.error("[Phoenix Socket] error:", err);
    });

    socket.connect();
    socketRef.current = socket;

    // ── Join agent:{userId} channel ───────────────────────────────────────────
    // AgentChannel.join/3 authenticates by comparing user.id == agent_id in the topic.
    // Managers/admins can join any agent's channel; agents can only join their own.
    const agentTopic = `agent:${user.id}`;
    const agentChannel = socket.channel(agentTopic, {});
    channelsRef.current.agent = agentChannel;

    agentChannel
      .join()
      .receive("ok", () => {
        setError(null);
        errorCountRef.current = 0;
      })
      .receive("error", (resp) => {
        console.error(`[Phoenix] Failed to join ${agentTopic}:`, resp);
        // Use same threshold as socket errors to avoid toast spam on page load
        errorCountRef.current += 1;
        if (errorCountRef.current >= 3) {
          setError("Failed to join agent channel.");
        }
      });

    // Map legacy Socket.io event names → Phoenix channel events
    agentChannel.on("agent:init", (data) => {
      setLastAgentStatus(data);
    });

    agentChannel.on("presence_diff", (data) => {
      setLastAgentStatus((prev) => ({ ...prev, presenceDiff: data }));
    });

    agentChannel.on("agent:raised_hand", (data) => {
      setLastAgentStatus((prev) => ({ ...prev, raisedHand: data }));
    });

    // Call status updates come through individual call channels;
    // agent:{userId} also broadcasts state_change for simplicity
    agentChannel.on("call:state_change", (data) => {
      setLastCallStatus(data);
    });

    agentChannel.on("ai:insight", (data) => {
      setLastAiInsight(data);
    });

    // Cleanup
    return () => {
      Object.values(channelsRef.current).forEach((ch) => ch.leave());
      channelsRef.current = {};
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [user?.id, hasSocketToken]);

  /**
   * Join a named Phoenix channel. Returns { channel, leave }.
   * Caller is responsible for calling leave() on unmount.
   *
   * @param {string} topic   e.g. "call:abc-123" or "campaign:xyz"
   * @param {object} params  optional join params
   * @param {object} handlers  { eventName: callbackFn }
   */
  const joinChannel = useCallback((topic, params = {}, handlers = {}) => {
    const socket = socketRef.current;
    if (!socket) return null;

    const channel = socket.channel(topic, params);

    channel
      .join()
      .receive("ok", () => {})
      .receive("error", (resp) =>
        console.error(`[Phoenix] Failed ${topic}:`, resp),
      );

    Object.entries(handlers).forEach(([event, cb]) => {
      channel.on(event, cb);
    });

    return {
      channel,
      leave: () => channel.leave(),
      push: (event, payload) => channel.push(event, payload),
    };
  }, []);

  /**
   * Push an event to the agent:{userId} channel.
   * Replaces socket.emit() pattern from Socket.io usage.
   */
  const emit = useCallback((event, payload = {}) => {
    const agentChannel = channelsRef.current.agent;
    if (!agentChannel) {
      return;
    }
    agentChannel.push(event, payload);
  }, []);

  /**
   * Subscribe to events on the agent:{userId} channel.
   * Returns an unsubscribe function.
   */
  const subscribe = useCallback((eventName, callback) => {
    const agentChannel = channelsRef.current.agent;
    if (!agentChannel) return () => {};
    const ref = agentChannel.on(eventName, callback);
    return () => agentChannel.off(eventName, ref);
  }, []);

  return {
    user,
    socket: socketRef.current,
    isConnected,
    error,
    lastCallStatus,
    lastAgentStatus,
    lastAiInsight,
    joinChannel,
    emit,
    subscribe,
  };
};

export default useSocket;
